/**
 * ClickToCall.jsx — Small click-to-call button with call count badge.
 * Usage: <ClickToCall phone="+15551234567" leadId="abc123" callCount={3} />
 */
import React from 'react';
import { Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useDialGuards } from '../hooks/useDialGuards.jsx';
import { useAuth } from '../store/auth.js';
import { openPhonePanel, setActiveCallGlobal } from './PhonePanel.jsx';
import * as tv from '../lib/twilioVoice.js';

export default function ClickToCall({ phone, leadId, callCount = 0, className = '' }) {
  const qc = useQueryClient();
  const agentId = useAuth((s) => s.agent?._id || s.user?._id);
  const { guardedClickToCall, guardModal } = useDialGuards();

  async function handleClick(e) {
    e.stopPropagation();
    if (!phone) { toast.error('No phone number'); return; }

    try {
      // callMode: 'browser' asks for the WebRTC softphone; the server also falls
      // back to it when no cell phone is configured.
      const res = await guardedClickToCall({
        phone,
        destinationNumber: phone,
        leadId,
        agentId,
        callMode: 'browser',
      });
      if (res?.cancelled) return;
      if (!res?.ok) {
        toast.error(res?.error || 'Call failed');
        return;
      }
      openPhonePanel();

      // Invalidate call history immediately so new call appears in database & UI
      qc.invalidateQueries({ queryKey: ['call-history'] });
      qc.invalidateQueries({ queryKey: ['phone-calls'] });
      if (leadId) {
        qc.invalidateQueries({ queryKey: ['lead', leadId] });
      }

      const { mode, token, callContext } = res.data || {};
      if (callContext?.callId) {
        setActiveCallGlobal({
          callId: callContext.callId,
          callSid: callContext.callSid || null,
          leadName: callContext.leadName || '',
          leadId: callContext.leadId || null,
          email: callContext.leadEmail || '',
          fromNumber: callContext.from || '',
          callState: 'ringing',
        });
      }

      if (mode === 'browser' && token) {
        try {
          if (!tv.isReady()) await tv.initVoice(token);
          await tv.placeCall(callContext?.to || phone, { callId: callContext?.callId || '' });
          toast.success(`Calling ${phone}…`);
        } catch (voiceErr) {
          console.warn('Voice connect warning:', voiceErr);
          toast.success(`Call initiated to ${phone}`);
        }
      } else if (mode === 'cell') {
        toast.success('Calling your cell to bridge…');
      } else if (mode === 'simulated') {
        toast('Dev mode: call simulated (Twilio Voice not configured)', { icon: '🧪' });
      } else if (mode === 'browser' && !token) {
        toast.error('Browser phone is not ready — check Phone settings and microphone access.');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'cell_phone_missing') {
        toast.error('Add a cell phone in Phone Panel settings, or switch to Browser mode.');
      } else {
        toast.error(data?.error || err.message || 'Call failed');
      }
    }
  }

  if (!phone) return null;

  return (
    <>
    {guardModal}
    <button
      onClick={handleClick}
      title={`Call ${phone}`}
      className={`relative inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-civix-purple/10 hover:bg-civix-purple/30 border border-civix-purple/20 hover:border-civix-purple/60 text-civix-purpleSoft transition-all ${className}`}
    >
      <Phone className="w-3.5 h-3.5" />
      <span className="text-xs font-medium hidden sm:inline">Call</span>
      {callCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-civix-purple rounded-full text-[9px] font-bold text-white flex items-center justify-center">
          {callCount > 9 ? '9+' : callCount}
        </span>
      )}
    </button>
    </>
  );
}
